// Pure, React-free helpers for the terminal split-pane tree. Ported from the
// SpliceTerminal reference and generalized so each pane carries its own host.

import type {
    LayoutNode,
    PaneKind,
    PaneNode,
    SplitNode,
} from '../types/terminal';

// Local (non-session) id generator for split nodes and tabs. Pane ids are
// backend session ids; these are layout-only and never leave the client.
let counter = 0;
export const localId = (prefix: string): string =>
    `${prefix}-${Date.now().toString(36)}-${(counter++).toString(36)}`;

// `kind` is only written when non-default so terminal panes serialize
// byte-identically to pre-SFTP layouts.
export const paneNode = (
    sessionId: string,
    hostId: string,
    kind?: PaneKind,
): PaneNode => ({
    type: 'pane',
    id: sessionId,
    hostId,
    ...(kind === 'sftp' ? { kind } : {}),
});

/**
 * Replace the pane `targetId` with a split of [target, newPane]. Returns a new
 * tree (structural sharing elsewhere). No-op if the target isn't found.
 */
export function splitTree(
    node: LayoutNode,
    targetId: string,
    dir: 'row' | 'col',
    newPane: PaneNode,
): LayoutNode {
    if (node.type === 'pane') {
        if (node.id !== targetId) return node;
        const split: SplitNode = {
            type: 'split',
            id: localId('s'),
            dir,
            sizes: [50, 50],
            children: [node, newPane],
        };
        return split;
    }
    return {
        ...node,
        children: [
            splitTree(node.children[0], targetId, dir, newPane),
            splitTree(node.children[1], targetId, dir, newPane),
        ],
    };
}

/**
 * Insert `newPane` next to the pane `targetId`, wrapping them in a new split.
 * `before` places the new pane on the leading side (left for `row`, top for
 * `col`); otherwise it goes after. Like {@link splitTree} but side-aware.
 */
export function insertBeside(
    node: LayoutNode,
    targetId: string,
    dir: 'row' | 'col',
    newPane: PaneNode,
    before: boolean,
): LayoutNode {
    if (node.type === 'pane') {
        if (node.id !== targetId) return node;
        const children: [LayoutNode, LayoutNode] = before
            ? [newPane, node]
            : [node, newPane];
        return { type: 'split', id: localId('s'), dir, sizes: [50, 50], children };
    }
    return {
        ...node,
        children: [
            insertBeside(node.children[0], targetId, dir, newPane, before),
            insertBeside(node.children[1], targetId, dir, newPane, before),
        ],
    };
}

/** Swap two panes' positions in the tree, keeping each pane's id/host intact. */
export function swapPanes(
    node: LayoutNode,
    a: PaneNode,
    b: PaneNode,
): LayoutNode {
    if (node.type === 'pane') {
        if (node.id === a.id) return b;
        if (node.id === b.id) return a;
        return node;
    }
    return {
        ...node,
        children: [
            swapPanes(node.children[0], a, b),
            swapPanes(node.children[1], a, b),
        ],
    };
}

/**
 * Remove the pane `targetId`. When a split loses a child it collapses to its
 * surviving child. Returns null if the whole tree was the removed pane.
 */
export function removeFromTree(
    node: LayoutNode,
    targetId: string,
): LayoutNode | null {
    if (node.type === 'pane') return node.id === targetId ? null : node;
    const a = removeFromTree(node.children[0], targetId);
    const b = removeFromTree(node.children[1], targetId);
    if (a && b) return { ...node, children: [a, b] };
    return a ?? b ?? null; // collapse the split onto the survivor
}

export function setSizes(
    node: LayoutNode,
    splitId: string,
    sizes: [number, number],
): LayoutNode {
    if (node.type === 'pane') return node;
    if (node.id === splitId) return { ...node, sizes };
    return {
        ...node,
        children: [
            setSizes(node.children[0], splitId, sizes),
            setSizes(node.children[1], splitId, sizes),
        ],
    };
}

/** Flatten to the list of panes (depth-first, left-to-right). */
export function collectPanes(node: LayoutNode, acc: PaneNode[] = []): PaneNode[] {
    if (node.type === 'pane') acc.push(node);
    else {
        collectPanes(node.children[0], acc);
        collectPanes(node.children[1], acc);
    }
    return acc;
}

export const findFirstPane = (node: LayoutNode): PaneNode =>
    node.type === 'pane' ? node : findFirstPane(node.children[0]);

/** Look up a pane by session id anywhere in the tree. */
export function findPane(node: LayoutNode, sessionId: string): PaneNode | null {
    if (node.type === 'pane') return node.id === sessionId ? node : null;
    return (
        findPane(node.children[0], sessionId) ??
        findPane(node.children[1], sessionId)
    );
}
